import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Typography, Tag, Card, Descriptions, Divider, Space, Table, Button, Progress, Alert, List, Avatar } from 'antd';
import { PrinterOutlined, CommentOutlined, LinkOutlined, UserOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../api';
import { formatTS } from '../utils/time';
import LinkifiedText from '../components/LinkifiedText';

// Print All Evaluations (Eval Progress > Client Report): every evaluation in
// ?ids=, one per printed page, laid out as an evaluation prints from its own
// page. ?comments=1 includes comments, ?hide_agent=1 then leaves out the
// agent's own, ?coaching=1 includes the coaching sessions list -- the same
// choices as the evaluation page's Print Options. Each evaluation is loaded
// through the evaluation page's own endpoints, so the same access rules apply;
// one this user cannot open is listed as skipped. Printing starts by itself
// once everything has loaded; Print is there to run it again.

const STATUS_LABEL = { assigned: 'Draft', in_progress: 'In Progress', completed: 'Completed',
                       rescore_requested: 'Rescore Requested', canceled: 'Canceled' };
const STATUS_COLOR = { assigned: 'blue', in_progress: 'orange', completed: 'green',
                       rescore_requested: 'purple', canceled: 'default' };
const SESSION_STATUS_COLOR = { draft: 'default', active: 'blue', completed: 'green', canceled: 'red' };
const SESSION_STATUS_LABEL = { draft: 'Draft', active: 'Active', completed: 'Completed', canceled: 'Canceled' };
const PARALLEL = 4;

function formatDuration(seconds) {
  if (seconds == null) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function questionMaxPts(q) {
  if (q.QuestionType === 'picklist') return Math.max(0, ...(q.Options || []).map((o) => o.Points));
  return q.MaxPoints || 0;
}

async function loadOne(id, withComments, withCoaching) {
  const res = await api.get(`/api/cases/${id}`);
  const c = res.data.case;
  const [comments, coaching] = await Promise.all([
    withComments ? api.get(`/api/cases/${id}/comments`).then((r) => r.data || []).catch(() => []) : [],
    withCoaching && c?.ContactID
      ? api.get(`/api/coaching/sessions/for-contact/${c.ContactID}`).then((r) => r.data || []).catch(() => [])
      : [],
  ]);
  return { id, caseData: c, form: res.data.form, occurrences: res.data.occurrences || {}, comments, coaching };
}

function EvalPrint({ ev, showComments, hideAgent, showCoaching }) {
  const { id, caseData, form, occurrences, comments, coaching } = ev;
  const responses = {};
  (form?.Sections || []).forEach((sec) => (sec.Questions || []).forEach((q) => {
    if (q.Response) {
      responses[q.ID] = { option_id: q.Response.OptionID ?? null,
                          response_text: q.Response.ResponseText ?? '',
                          points_earned: q.Response.PointsEarned ?? 0 };
    }
  }));
  let totalEarned = 0; let totalMax = 0;
  (form?.Sections || []).forEach((sec) => (sec.Questions || []).forEach((q) => {
    totalEarned += responses[q.ID]?.points_earned ?? 0;
    totalMax += questionMaxPts(q);
  }));
  const pct = totalMax > 0 ? Math.round((totalEarned / totalMax) * 100) : 0;
  // Points can have decimals (2.5); sums are shown to 2 places.
  const fmtPts = (v) => Math.round((v || 0) * 100) / 100;
  const isEval = !caseData?.IsFeedbackOnly && !caseData?.IsCalibration;
  const shownComments = (comments || []).filter((c) => !(hideAgent && c.UserID === caseData?.AgentUserID));

  return (
    <div>
      <div style={{ marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid #000' }}>
        <div><strong>Evaluation #{id} &mdash; Status: {STATUS_LABEL[caseData?.Status] ?? caseData?.Status}</strong></div>
        <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>Printed: {dayjs().format('YYYY-MM-DD HH:mm')}</div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        {caseData?.IsCalibration ? <Tag color="purple" style={{ fontSize: 14, fontWeight: 700 }}>Calibration</Tag>
          : caseData?.IsFeedbackOnly ? <Tag color="purple" style={{ fontSize: 14, fontWeight: 700 }}>Feedback Only</Tag>
            : <Tag color="blue" style={{ fontSize: 14, fontWeight: 700 }}>Evaluation</Tag>}
        <Typography.Title level={4} style={{ margin: 0 }}>#{id} — {caseData && !caseData.ContactID ? `Ref ${caseData.ExternalRef || '—'}` : `Contact ${caseData?.ContactID || '—'}`}</Typography.Title>
        <Tag color={STATUS_COLOR[caseData?.Status] ?? 'default'} style={{ fontSize: 13 }}>
          Status: {STATUS_LABEL[caseData?.Status] ?? caseData?.Status}
        </Tag>
        {caseData?.CreatedAt && <Typography.Text type="secondary" style={{ fontSize: 13 }}>Created on: {formatTS(caseData.CreatedAt)}</Typography.Text>}
        {caseData?.UpdatedAt && <Typography.Text type="secondary" style={{ fontSize: 13 }}>Last modified: {formatTS(caseData.UpdatedAt)}</Typography.Text>}
        {caseData?.CompletedAt && <Typography.Text type="secondary" style={{ fontSize: 13 }}>Completed on: {formatTS(caseData.CompletedAt)}</Typography.Text>}
        {caseData?.RescoreRequestedAt && <Typography.Text type="secondary" style={{ fontSize: 13 }}>Rescore requested on: {formatTS(caseData.RescoreRequestedAt)}</Typography.Text>}
      </div>

      {form && (
        <div style={{ marginBottom: 12 }}>
          <Typography.Text strong style={{ fontSize: 14 }}>Form: {form.Name}{form.Version ? ` (v${form.Version})` : ''}</Typography.Text>
        </div>
      )}

      <Descriptions size="small" bordered column={4} style={{ marginBottom: 16 }}
        labelStyle={{ width: 80, whiteSpace: 'nowrap' }} contentStyle={{ wordBreak: 'break-word' }}>
        <Descriptions.Item label="Agent">{caseData?.AgentName || '—'}</Descriptions.Item>
        <Descriptions.Item label="Call Date">{caseData?.CallDate?.slice(0, 10) || '—'}</Descriptions.Item>
        <Descriptions.Item label="Campaign">{caseData?.Campaign || '—'}</Descriptions.Item>
        <Descriptions.Item label="Skill">{caseData?.Skill || '—'}</Descriptions.Item>
        <Descriptions.Item label="Duration">{formatDuration(caseData?.AgentTime)}</Descriptions.Item>
        <Descriptions.Item label="Media">{caseData?.MediaName || '—'}</Descriptions.Item>
        <Descriptions.Item label="Disposition">{caseData?.Disposition || '—'}</Descriptions.Item>
        <Descriptions.Item label="Direction">{caseData?.Direction || '—'}</Descriptions.Item>
        <Descriptions.Item label="End Reason">{caseData?.ContactEndReason || '—'}</Descriptions.Item>
        <Descriptions.Item label="Assigned To">{caseData?.AssignedToName || '—'}</Descriptions.Item>
        <Descriptions.Item label="External Ref" span={2}>{caseData?.ExternalRef || '—'}</Descriptions.Item>
        {caseData?.RescoreNote && (
          <Descriptions.Item label="Dispute Reason" span={2}><strong>{caseData.RescoreNote}</strong></Descriptions.Item>
        )}
      </Descriptions>

      {form ? (
        <>
          <Card style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <div>
                <Typography.Text strong style={{ fontSize: 15 }}>{form.Name}</Typography.Text>
                {form.Version && <Tag style={{ marginLeft: 8 }}>v{form.Version}</Tag>}
              </div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <Typography.Text type="secondary">Score</Typography.Text>
                  <Typography.Text strong>{fmtPts(totalEarned)} / {fmtPts(totalMax)} pts ({pct}%)</Typography.Text>
                </div>
                <Progress percent={pct} showInfo={false} size="small" />
              </div>
            </div>
          </Card>

          {(form.Sections || []).map((sec) => {
            const secMax    = (sec.Questions || []).reduce((s, q) => s + questionMaxPts(q), 0);
            const secEarned = (sec.Questions || []).reduce((s, q) => s + (responses[q.ID]?.points_earned ?? 0), 0);
            return (
              <Card key={sec.ID} size="small" style={{ marginBottom: 12 }} styles={{ body: { background: '#f0f7ff' } }}
                title={<Space><span>{sec.Name}</span><Tag color="blue">{fmtPts(secEarned)} / {fmtPts(secMax)} pts</Tag></Space>}>
                {isEval && secMax > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <Typography.Text style={{ fontSize: 13 }}>
                      Occurrence: <strong>{(occurrences[sec.ID] ?? 0) === 1 ? 'Yes' : 'No'}</strong>
                    </Typography.Text>
                  </div>
                )}
                {(sec.Questions || []).map((q, qi) => {
                  const r = responses[q.ID];
                  return (
                    <div key={q.ID}>
                      {qi > 0 && <Divider style={{ margin: '14px 0', borderColor: '#cce0ff' }} />}
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: q.Guidance ? 4 : 8 }}>
                        <Space size={4}>
                          <Typography.Text strong>{q.QuestionText}</Typography.Text>
                          {q.IsMandatory ? <Typography.Text type="danger">*</Typography.Text> : null}
                        </Space>
                        <Tag>{r?.points_earned ?? 0} / {questionMaxPts(q)} pts</Tag>
                      </div>
                      {q.Guidance && (
                        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>{q.Guidance}</Typography.Text>
                      )}
                      {q.QuestionType === 'picklist' ? (
                        <div>
                          <Space direction="vertical" size={2} style={{ marginBottom: 4 }}>
                            {(q.Options || []).map((opt) => {
                              const sel = opt.ID === r?.option_id;
                              return (
                                <div key={opt.ID} style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: sel ? 1 : 0.4 }}>
                                  <span style={{ width: 13, height: 13, borderRadius: '50%', display: 'inline-block', flexShrink: 0,
                                                 border: `2px solid ${sel ? '#1677ff' : '#bfbfbf'}`, background: sel ? '#1677ff' : 'transparent' }} />
                                  <Typography.Text style={{ fontWeight: sel ? 600 : 400 }}>{opt.OptionText}</Typography.Text>
                                  <Tag style={{ marginLeft: 2 }}>{opt.Points} pts</Tag>
                                </div>
                              );
                            })}
                          </Space>
                          {r?.response_text?.trim() && (
                            <Typography.Text style={{ display: 'block', marginTop: 6, color: '#595959', whiteSpace: 'pre-wrap', fontSize: 13 }}>{r.response_text}</Typography.Text>
                          )}
                        </div>
                      ) : q.QuestionType === 'text' ? (
                        <Typography.Text style={{ whiteSpace: 'pre-wrap' }}>{r?.response_text?.trim() || '—'}</Typography.Text>
                      ) : q.QuestionType === 'date' ? (
                        <Typography.Text>{r?.response_text || '—'}</Typography.Text>
                      ) : (
                        <div>
                          <Typography.Text strong>{r?.points_earned ?? 0} / {q.MaxPoints} pts</Typography.Text>
                          {r?.response_text?.trim() && (
                            <Typography.Text style={{ display: 'block', marginTop: 6, color: '#595959', whiteSpace: 'pre-wrap', fontSize: 13 }}>{r.response_text}</Typography.Text>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </Card>
            );
          })}
        </>
      ) : (
        <Typography.Text type="secondary">No evaluation form is attached to this evaluation.</Typography.Text>
      )}

      {showComments && (
        <>
          <Divider orientation="left"><Space size={6}><CommentOutlined /><span>Comments</span></Space></Divider>
          {shownComments.length > 0 && (
            <List
              dataSource={shownComments}
              renderItem={(c) => (
                <List.Item style={{ alignItems: 'flex-start', padding: '8px 0' }}>
                  <List.Item.Meta
                    avatar={<Avatar icon={<UserOutlined />} size="small" style={{ backgroundColor: '#1677ff', marginTop: 2 }} />}
                    title={<Space size={8}>
                      <Typography.Text strong style={{ fontSize: 13 }}>{c.UserName}</Typography.Text>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>{formatTS(c.CreatedAt)}</Typography.Text>
                    </Space>}
                    description={<Typography.Text strong={!!c.IsSystem} style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}><LinkifiedText text={c.Comment} /></Typography.Text>}
                  />
                </List.Item>
              )}
            />
          )}
        </>
      )}

      {showCoaching && (
        <>
          <Divider orientation="left"><Space size={6}><LinkOutlined /><span>Coaching Sessions</span></Space></Divider>
          <Table
            rowKey="ID"
            dataSource={coaching}
            size="small"
            pagination={false}
            locale={{ emptyText: 'No coaching sessions linked to this evaluation.' }}
            columns={[
              { title: 'Title', dataIndex: 'Title', key: 'Title' },
              { title: 'Status', dataIndex: 'Status', key: 'Status', width: 100,
                render: (v) => <Tag color={SESSION_STATUS_COLOR[v] ?? 'default'}>{SESSION_STATUS_LABEL[v] ?? v}</Tag> },
              { title: 'Request Type', dataIndex: 'RequestTypeName', key: 'RequestTypeName', width: 160 },
              { title: 'Coach', dataIndex: 'CoachName', key: 'CoachName', width: 160 },
              { title: 'Due Date', dataIndex: 'DueDate', key: 'DueDate', width: 110 },
            ]}
          />
        </>
      )}
    </div>
  );
}

export default function EvalPrintAllPage() {
  const [params] = useSearchParams();
  const ids = useMemo(() => (params.get('ids') || '').split(',').map((x) => parseInt(x, 10)).filter((x) => x > 0), [params]);
  const showComments = params.get('comments') === '1';
  const hideAgent    = showComments && params.get('hide_agent') === '1';
  const showCoaching = params.get('coaching') === '1';

  const [loaded,  setLoaded]  = useState({});   // id -> ev
  const [failed,  setFailed]  = useState([]);   // ids that could not be opened
  const [done,    setDone]    = useState(false);
  const printed = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const queue = [...ids];
      const worker = async () => {
        while (queue.length && !cancelled) {
          const id = queue.shift();
          try {
            const ev = await loadOne(id, showComments, showCoaching);
            if (!cancelled) setLoaded((m) => ({ ...m, [id]: ev }));
          } catch {
            if (!cancelled) setFailed((f) => [...f, id]);
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(PARALLEL, ids.length) }, worker));
      if (!cancelled) setDone(true);
    })();
    return () => { cancelled = true; };
  }, [ids, showComments, showCoaching]);

  useEffect(() => {
    if (done && !printed.current && Object.keys(loaded).length) {
      printed.current = true;
      setTimeout(() => window.print(), 600);
    }
  }, [done, loaded]);

  const count = Object.keys(loaded).length + failed.length;
  const ready = ids.filter((id) => loaded[id]);

  return (
    <div>
      <style>{`
        @media print {
          html, body { margin: 0 !important; padding: 0 !important; background: white !important; }
          .ant-layout { min-height: 0 !important; background: white !important; }
          .ant-layout-header, .ant-layout-sider { display: none !important; }
          .ant-layout-content { padding: 0 !important; margin: 0 !important; background: white !important; }
          .no-print { display: none !important; }
          .ant-card { break-inside: avoid; page-break-inside: avoid; }
          .eval-print-page { break-after: page; page-break-after: always; }
          .eval-print-page:last-child { break-after: auto; page-break-after: auto; }
          .ant-descriptions-bordered .ant-descriptions-view table { table-layout: auto !important; width: 100% !important; }
          .ant-descriptions-bordered .ant-descriptions-item-label,
          .ant-descriptions-bordered .ant-descriptions-item-content {
            white-space: normal !important; word-break: break-word !important; min-width: 60px !important; padding: 4px 8px !important;
          }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
      `}</style>

      <div className="no-print" style={{ marginBottom: 16 }}>
        <Space align="center" wrap>
          <Typography.Title level={4} style={{ margin: 0 }}>Print All Evaluations</Typography.Title>
          <Button type="primary" icon={<PrinterOutlined />} disabled={!done} onClick={() => window.print()}>Print</Button>
        </Space>
        {!done && (
          <div style={{ maxWidth: 400, marginTop: 8 }}>
            <Typography.Text type="secondary">Loading {count} of {ids.length}…</Typography.Text>
            <Progress percent={ids.length ? Math.round((count / ids.length) * 100) : 100} size="small" />
          </div>
        )}
        {done && failed.length > 0 && (
          <Alert type="warning" showIcon style={{ marginTop: 8 }}
            message={`${failed.length} evaluation${failed.length === 1 ? '' : 's'} could not be opened and ${failed.length === 1 ? 'is' : 'are'} left out: #${failed.join(', #')}`} />
        )}
        {ids.length === 0 && <Alert type="info" showIcon style={{ marginTop: 8 }} message="No evaluations to print." />}
      </div>

      {done && ready.map((id) => (
        <div key={id} className="eval-print-page" style={{ marginBottom: 32 }}>
          <EvalPrint ev={loaded[id]} showComments={showComments} hideAgent={hideAgent} showCoaching={showCoaching} />
        </div>
      ))}
    </div>
  );
}
